export const AUTH_ROUTES = {
  SIGN_IN: "/sign-in",
  FORGOT_PASSWORD: "/forgot-password",
  RESET_PASSWORD: "/reset-password",
} as const;

export const PROTECTED_ROUTES = {
  DASHBOARD: "/dashboard",
  PROFILE: "/profile",
  GAMIFICATION_CHALLENGES: "/challenges",
  GAMIFICATION_RANKING: "/ranking",
  STAFF: "/staff",
  STAFF_NEW: "/staff/new",
  STAFF_DETAIL: "/staff/$staffMemberId",
  STAFF_EDIT: "/staff/$staffMemberId/edit",
  ATHLETES: "/athletes",
  ATHLETE_DETAIL: "/athletes/$athleteId",
  ATHLETE_NEW: "/athletes/new",
  ATHLETE_EDIT: "/athletes/$athleteId/edit",
  ATHLETE_WORKOUT_PLAN_NEW: "/athletes/$athleteId/workout-plans/new",
  ATHLETE_WORKOUT_PLAN_EDIT:
    "/athletes/$athleteId/workout-plans/$workoutPlanId/edit",
  ATHLETE_WORKOUT_BLOCK_NEW: "/athletes/$athleteId/workout-blocks/new",
  ATHLETE_WORKOUT_BLOCK_DETAIL:
    "/athletes/$athleteId/workout-blocks/$workoutBlockId",
  ATHLETE_WORKOUT_BLOCK_EDIT:
    "/athletes/$athleteId/workout-blocks/$workoutBlockId/edit",
  CALENDAR: "/allocation",
  GYM_SCHEDULES: "/schedules",
  CALENDAR_DETAIL: "/allocation/$eventId",
  CALENDAR_NEW_EVENT: "/allocation/events/new",
  CALENDAR_EDIT_EVENT: "/allocation/$eventId/edit",
  LOGISTIC_DISCIPLINES: "/disciplines",
  LOGISTIC_EXERCISES: "/exercises",
  LOGISTIC_ROOMS: "/rooms",
  LOGISTIC_NEW_ROOM: "/rooms/new",
  LOGISTIC_ROOMS_DETAIL: "/rooms/$roomId",
} as const;

export const MANAGEMENT_ROUTES = {
  MANAGEMENT_DASHBOARD: "/management/dashboard",
  DISCIPLINES: "/management/disciplines",
  DISCIPLINE_DETAIL: "/management/disciplines/$disciplineId",
  NEW_DISCIPLINE: "/management/disciplines/new",
  EXERCISES: "/management/exercises",
  EXERCISE_DETAIL: "/management/exercises/$exerciseId",
  NEW_EXERCISE: "/management/exercises/new",
} as const;

export const ROOT_ROUTES = {
  HOME: "/",
} as const;

export const ROUTES = {
  ...AUTH_ROUTES,
  ...PROTECTED_ROUTES,
  ...MANAGEMENT_ROUTES,
  ...ROOT_ROUTES,
} as const;

export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];

export const buildRoute = {
  gymScheduleEdit: (scheduleId: number) => `/schedules/${scheduleId}`,
  gymScheduleBlueprintEdit: (scheduleId: number) =>
    `/schedules/${scheduleId}/edit`,
  calendarEventDetail: (eventId: number): string => `/allocation/${eventId}`,
  staffProfile: (id: number) => `/staff/${id}`,
  staffEdit: (id: number): string => `/staff/${id}/edit`,
  athleteDetail: (athleteId: string): string => `/athletes/${athleteId}`,
  athleteEdit: (athleteId: string): string => `/athletes/${athleteId}/edit`,
  athleteWorkoutBlockNew: (athleteId: string): string =>
    `/athletes/${athleteId}/workout-blocks/new`,
  athleteWorkoutBlockDetail: (athleteId: string, blockId: string): string =>
    `/athletes/${athleteId}/workout-blocks/${blockId}`,
  athleteWorkoutBlockEdit: (athleteId: string, blockId: string): string =>
    `/athletes/${athleteId}/workout-blocks/${blockId}/edit`,
  athleteWorkoutPlanNew: (athleteId: string): string =>
    `/athletes/${athleteId}/workout-plans/new`,
  disciplineDetail: (disciplineId: string) =>
    `/management/disciplines/${disciplineId}`,
  roomDetail: (roomId: string) => `/rooms/${roomId}`,
  exerciseDetail: (exerciseId: string) => `/management/exercises/${exerciseId}`,
} as const;

// A nav path is "active" on an exact match or on any of its sub-routes
// (e.g. `/staff` stays active on `/staff/42/edit`).
export function isPathActive(
  path: string | undefined,
  pathname: string,
): boolean {
  return !!path && (path === pathname || pathname.startsWith(`${path}/`));
}

export type ScheduleReturnRoute =
  | { to: string; search: { tab: "calendar"; week: string | undefined } }
  | {
      to: typeof PROTECTED_ROUTES.CALENDAR;
      search: { week: string | undefined };
    };

export function buildScheduleReturnRoute(
  staffProfileId: number | undefined,
  week: string | undefined,
): ScheduleReturnRoute {
  if (staffProfileId !== undefined) {
    return {
      to: buildRoute.staffProfile(staffProfileId),
      search: { tab: "calendar", week },
    };
  }
  return { to: PROTECTED_ROUTES.CALENDAR, search: { week } };
}

export function resolveScheduleReturnStaffMemberId(
  contextStaffMemberId: number | undefined,
  formStaffMemberId: number | undefined,
): number | undefined {
  if (contextStaffMemberId === undefined) return undefined;
  return formStaffMemberId ?? contextStaffMemberId;
}
